import { ReactNode } from 'react';
import { genericMemo } from '@direct-frontend/stdlib';
import { useLogger } from '@direct-frontend/modules-logger';
import { DOMProps } from '@react-types/shared';

import { IconBurgerMenu } from '../../icons';
import { cn } from '../../lib/css/className/className';
import { TestProps } from '../../internal/types/testTypes';
import { HStack } from '../Stack';
import { ClickableIcon } from '../ClickableIcon';
import { Badge } from '../Badge';

import './NavigationBar.css';

const cls = cn('NavigationBar');

export interface NavigationBarProps extends TestProps, DOMProps {
    /**
     * Дополнительный класс для основного элемента
     */
    className?: string;
    /**
     * Логотип приложения
     */
    logo: ReactNode;
    /**
     * Дополнительные элементы (например: чат, баланс и т.п.)
     */
    addonRight?: ReactNode;
    /**
     * Количество уведомлений
     */
    notificationCount?: number;
    /**
     * Коллбэк, вызываемый при клике на меню
     */
    onMenuClick: () => void;
}

const MAX_NOTIFICATION_COUNT = 99;

export const NavigationBar = genericMemo(function NavigationBar({
    logo,
    addonRight,
    notificationCount,
    className,
    onMenuClick,
    'data-testid': dataTestId = 'NavigationBar',
}: NavigationBarProps) {
    const { LogProvider } = useLogger('NavigationBar');

    return (
        <LogProvider>
            <HStack
                data-testid={dataTestId}
                className={cls({}, [className])}
                alignItems="center"
                justifyContent="space-between"
            >
                {logo}
                <HStack className={cls('content')} alignItems="center">
                    <div className={cls('addon-right')}>{addonRight}</div>
                    <div className={cls('menu')}>
                        <ClickableIcon
                            clickableAreaSize="44"
                            onClick={onMenuClick}
                            data-testid={`${dataTestId}.BurgerMenuIcon`}
                        >
                            <IconBurgerMenu size="24" />
                        </ClickableIcon>
                        {notificationCount ? (
                            <Badge size="16" color="info" className={cls('menu-badge')}>
                                {notificationCount > MAX_NOTIFICATION_COUNT
                                    ? `${MAX_NOTIFICATION_COUNT}+`
                                    : notificationCount}
                            </Badge>
                        ) : null}
                    </div>
                </HStack>
            </HStack>
        </LogProvider>
    );
});
